import { useEffect, useState } from 'react';

/**
 * Tracks cumulative time-on-site for anonymous (not-signed-in) visitors.
 * - Stored in localStorage so it persists across tabs, refreshes, and days (absolute).
 * - Only counts seconds while the page is visible.
 * - Stops counting the moment the user signs in.
 *
 * Exposes two thresholds:
 *    after 5 minutes → `showPrompt = true`  (soft, dismissible banner)
 *    after 10 minutes → `showBlock = true`   (hard paywall — must sign up to proceed)
 */
const KEY = 'fls_anon_seconds_v1';
const PROMPT_AFTER = 5 * 60;    // 5:00 — soft dismissible corner banner
const WARN_AFTER = 9 * 60 + 30; // 9:30 — friendly "30 seconds left" heads-up
const BLOCK_AFTER = 10 * 60;    // 10:00 — hard paywall

function read() {
  try { return Math.max(0, parseInt(localStorage.getItem(KEY) || '0', 10)) || 0; } catch { return 0; }
}
function write(n) {
  try { localStorage.setItem(KEY, String(Math.max(0, Math.floor(n)))); } catch (_) {}
}

export function resetAnonTimer() {
  try { localStorage.removeItem(KEY); } catch (_) {}
}

export function useAnonUsageTimer(signedIn) {
  const [secs, setSecs] = useState(() => read());

  useEffect(() => {
    if (signedIn) {
      // Once they register/sign-in, reset the counter so they start fresh
      resetAnonTimer();
      setSecs(0);
      return;
    }
    // Tick every second while visible
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      const next = read() + 1;
      write(next);
      setSecs(next);
    };
    const id = setInterval(tick, 1000);
    // Re-sync on tab focus so a long-idle tab doesn't double-count
    const onFocus = () => setSecs(read());
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [signedIn]);

  return {
    secs,
    showPrompt: !signedIn && secs >= PROMPT_AFTER && secs < WARN_AFTER,
    showWarning: !signedIn && secs >= WARN_AFTER && secs < BLOCK_AFTER,
    showBlock: !signedIn && secs >= BLOCK_AFTER,
    secondsUntilBlock: Math.max(0, BLOCK_AFTER - secs),
    minutes: Math.floor(secs / 60),
  };
}
